import React, { useState } from 'react'
import Select from 'react-select'

import {
  PhoneField,
  PhoneFieldProps,
  TextField,
  TextFieldProps
} from '../kit/form'
import { colors, fonts } from '@theme'
import Box, { BoxProps } from '../kit/box'
import ReactSwitch, { ReactSwitchProps } from 'react-switch'
import Text, { TextProps } from '../kit/text'
import Button, { ButtonProps } from '../kit/button'

/* -----------------------------------------------------------------------------
 * InputFieldFormated
 * -------------------------------------------------------------------------- */

interface InputFieldFormatedProps {
  name: string
  value: string
  label: string
  valuePlaceholder?: string
  onChangeText: (text: string) => void
  compact?: boolean
  fieldPrefix?: React.ReactNode
  FieldComponent: React.ComponentType<any>
}

const InputFieldFormated = ({
  name,
  value,
  label,
  valuePlaceholder,
  onChangeText,
  FieldComponent,
  ...otherProps
}: InputFieldFormatedProps) => {
  const [isSelected, setIsSelected] = useState(false)

  return (
    <FieldComponent
      name={name}
      value={
        !!valuePlaceholder && value?.length == 0 && !isSelected
          ? valuePlaceholder
          : value
      }
      label={label}
      onChangeText={onChangeText}
      onFocus={() => setIsSelected(true)}
      onBlur={() => setIsSelected(false)}
      required
      mb='04'
      borderColor={isSelected ? 'walrusBlue500' : 'transparent'}
      borderWidth={2}
      textColor={
        isSelected
          ? 'walrusBlue500'
          : value?.length > 0
          ? 'walrusBlue200'
          : 'shade06'
      }
      font='display.14.bold'
      placeholderFont='display.12.bold'
      labelFont='display.12.bold'
      placeholderTextColor='shade05'
      labelTextColor={
        value?.length > 0 || isSelected ? '#404CABCC' : 'walrusBlue700'
      }
      backgroundColor={isSelected ? 'white' : 'walrusBlue050'}
      {...otherProps}
    />
  )
}

/* -----------------------------------------------------------------------------
 * TextFieldFormated
 * -------------------------------------------------------------------------- */

interface TextFieldFormatedProps extends TextFieldProps {
  name: string
  value: string
  label: string
  valuePlaceholder?: string
  onChangeText: (text: string) => void
}

const TextFieldFormated = ({
  name,
  value,
  label,
  valuePlaceholder,
  onChangeText,
  ...otherProps
}: TextFieldFormatedProps) => {
  return (
    <InputFieldFormated
      FieldComponent={TextField}
      name={name}
      value={value}
      label={label}
      valuePlaceholder={valuePlaceholder}
      onChangeText={onChangeText}
      {...otherProps}
    />
  )
}

/* -----------------------------------------------------------------------------
 * PhoneFieldFormated
 * -------------------------------------------------------------------------- */

interface PhoneFieldFormatedProps extends PhoneFieldProps {
  name: string
  value: string
  label: string
  onChangeText: (text: string) => void
}

const PhoneFieldFormated = ({
  name,
  value,
  label,
  onChangeText,
  ...otherProps
}: PhoneFieldFormatedProps) => {
  return (
    <InputFieldFormated
      FieldComponent={PhoneField}
      name={name}
      value={value}
      label={label}
      onChangeText={onChangeText}
      {...otherProps}
    />
  )
}

/* -----------------------------------------------------------------------------
 * SelectFieldFormated
 * -------------------------------------------------------------------------- */

interface SelectFieldFormatedProps {
  name?: string
  options?: any[]
  value?: any
  defaultValue?: any
  onChange?: (value: any, event: any) => void
  styles?: any
  disabled?: boolean
  props?: any
  [key: string]: any
}

const SelectFieldFormated = ({
  name,
  options,
  value,
  defaultValue,
  onChange,
  styles,
  disabled = false,
  ...props
}: SelectFieldFormatedProps) => {
  const controlStyles = styles?.control || {}
  const optionStyles = styles?.option || {}
  const singleValueStyles = styles?.singleValue || {}
  const indicatorSeparatorStyles = styles?.indicatorSeparator || {}

  const otherStyles = Object.keys(styles || {}).reduce((acc, key) => {
    if (
      !['control', 'option', 'singleValue', 'indicatorSeparator'].includes(key)
    ) {
      acc[key] = styles[key]
    }
    return acc
  }, {})

  return (
    <Select
      name={name}
      options={options}
      isDisabled={disabled}
      value={value}
      defaultValue={defaultValue}
      onChange={onChange}
      styles={{
        control: (provided, state) => ({
          ...provided,
          fontFamily: fonts['display.14.bold'].fontFamily,
          fontSize: fonts['display.14.bold'].fontSize,
          fontWeight: fonts['display.14.bold'].fontWeight,
          color: colors.walrusBlue700,
          boxShadow: 'none',
          border: 'none',
          backgroundColor: controlStyles.backgroundColor
            ? controlStyles.backgroundColor
            : 'white',
          width: '100%',
          ...controlStyles
        }),
        option: (provided, state) => ({
          ...provided,
          fontFamily: fonts['display.14.bold'].fontFamily,
          fontSize: fonts['display.14.bold'].fontSize,
          fontWeight: fonts['display.14.bold'].fontWeight,
          color: state.isSelected ? colors.walrusBlue500 : colors.walrusBlue700,
          backgroundColor: controlStyles.backgroundColor
            ? controlStyles.backgroundColor
            : 'white',
          ...optionStyles
        }),
        singleValue: (provided, state) => ({
          ...provided,
          fontFamily: fonts['display.14.bold'].fontFamily,
          fontSize: fonts['display.14.bold'].fontSize,
          fontWeight: fonts['display.14.bold'].fontWeight,
          color: colors.walrusBlue700,
          ...singleValueStyles
        }),
        indicatorSeparator: (provided, state) => ({
          ...provided,
          display: 'none',
          ...indicatorSeparatorStyles
        }),
        ...otherStyles
      }}
      {...props}
    />
  )
}

/* -----------------------------------------------------------------------------
 * RadioInputFormated
 * -------------------------------------------------------------------------- */

interface RadioInputFormatedProps {
  name: string
  value: string
  selectedValue: string
  onChange: (value: string) => void
}

const RadioInputFormated = ({
  name,
  value,
  selectedValue,
  onChange,
  ...props
}: RadioInputFormatedProps) => {
  return (
    <Box
      borderRadius='pill'
      borderWidth={2}
      borderColor='walrusBlue200'
      background='walrusBlue200'
      height={14}
      width={14}
      p='00'
      m='00'
      position='relative'
      justifyContent='center'
      alignItems='center'
    >
      <input
        name={name}
        type='radio'
        value={value}
        checked={selectedValue === value}
        onChange={() => onChange(value)}
        style={{
          position: 'absolute',
          width: 12,
          height: 12,
          padding: 0,
          margin: 0,
          accentColor: colors.walrusBlue200
        }}
        {...props}
      />
    </Box>
  )
}

interface SwitchInputFormatedProps
  extends Omit<ReactSwitchProps, 'onChange' | 'checked'> {
  onChange: (checked: boolean) => void
  checked: boolean
  label?: string
  labelColor?: keyof typeof colors
  labelFont?: keyof typeof fonts
  labelPosition?: 'left' | 'right' | 'top' | 'bottom'
  alignItems?: 'center' | 'flex-start' | 'flex-end'
  justifyContent?: 'center' | 'flex-start' | 'flex-end'
  restProps?: any
}

const SwitchInputFormated = ({
  onChange,
  checked,
  label,
  labelColor = 'walrusBlue700',
  labelPosition = 'right',
  labelFont = 'text.12.bold',
  alignItems = 'center',
  justifyContent = 'center',
  ...restProps
}: SwitchInputFormatedProps) => {
  let flexDirection: 'row' | 'row-reverse' | 'column' | 'column-reverse' = 'row'
  switch (labelPosition) {
    case 'left':
      flexDirection = 'row'
      break
    case 'right':
      flexDirection = 'row-reverse'
      break
    case 'top':
      flexDirection = 'column'
      break
    case 'bottom':
      flexDirection = 'column-reverse'
      break
    default:
      flexDirection = 'row'
  }

  return (
    <Box
      flexDirection={flexDirection}
      gap='08'
      style={{
        flexShrink: 1,
        width: 'fit-content'
      }}
      alignItems={alignItems}
      justifyContent={justifyContent}
    >
      {label && (
        <Text font={labelFont} textColor={labelColor}>
          {label}
        </Text>
      )}

      <ReactSwitch
        onColor={colors.walrusBlue100}
        onHandleColor={colors.walrusBlue500}
        offColor={colors.walrusBlue100}
        offHandleColor={colors.walrusBlue050}
        handleDiameter={20}
        height={24}
        width={40}
        uncheckedIcon={false}
        checkedIcon={false}
        onChange={onChange}
        checked={checked}
        {...restProps}
      />
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * FormButtonPanel
 * -------------------------------------------------------------------------- */

interface FormButtonPanelProps {
  onCancel: () => void
  onSave: () => void
  fullWidth?: boolean
  inverted?: boolean
  saveLabel?: string
  cancelLabel?: string
  saveVariant?: ButtonProps['variant']
  cancelVariant?: ButtonProps['variant']
  saveSize?: ButtonProps['size']
  cancelSize?: ButtonProps['size']
  paddingTop?: BoxProps['paddingTop']
  gap?: BoxProps['gap']
  disabled?: boolean
}

function FormButtonPanel({
  onCancel,
  onSave,
  fullWidth = true,
  inverted = false,
  saveLabel = 'Save',
  cancelLabel = 'Cancel',
  saveVariant = 'violetGradient',
  cancelVariant = 'activeGray',
  saveSize = 'mediumSmall',
  cancelSize = 'mediumSmall',
  paddingTop = '20',
  gap = '12',
  disabled = false
}: FormButtonPanelProps) {
  return (
    <Box
      width={fullWidth ? '100%' : 'fit-content'}
      flexDirection={inverted ? 'row-reverse' : 'row'}
      gap={gap}
      justifyContent='flex-end'
      paddingTop={paddingTop}
    >
      <Button
        onPress={onCancel}
        size={cancelSize}
        variant={cancelVariant}
        disabled={disabled}
      >
        {cancelLabel}
      </Button>
      <Button
        onPress={onSave}
        size={cancelSize}
        variant={saveVariant}
        disabled={disabled}
      >
        {saveLabel}
      </Button>
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * FormDivider
 * -------------------------------------------------------------------------- */

interface FormDividerProps {
  children?: React.ReactNode
  dividerColor?: keyof typeof colors
  dividerHeight?: BoxProps['height']
  marginY?: BoxProps['marginY']
  gap?: BoxProps['gap']
  textColor?: TextProps['textColor']
  textVariant?: TextProps['variant']
}

function FormDivider({
  children,
  dividerColor = 'shade03',
  dividerHeight = '1px',
  marginY = '12',
  gap = '12',
  textColor = 'shade04',
  textVariant = 'meta'
}: FormDividerProps) {
  if (!children) {
    return (
      <Box
        height={dividerHeight}
        backgroundColor={dividerColor}
        marginY={marginY}
        width='100%'
      />
    )
  }

  return (
    <Box flexDirection='row' gap={gap} alignItems='center' marginY={marginY}>
      <Box
        flex='1 1 auto'
        height={dividerHeight}
        backgroundColor={dividerColor}
      />
      <Text variant={textVariant} textColor={textColor}>
        {children}
      </Text>
      <Box
        flex='1 1 auto'
        height={dividerHeight}
        backgroundColor={dividerColor}
      />
    </Box>
  )
}

export {
  InputFieldFormated,
  TextFieldFormated,
  PhoneFieldFormated,
  SelectFieldFormated,
  RadioInputFormated,
  SwitchInputFormated,
  FormButtonPanel,
  FormDivider
}
